/**
 * EngagementDriverSection Component
 *
 * Specialized UI for the Engagement Context section in Business Scoping.
 * Provides a visually distinct treatment to set the tone for the assessment
 * and explain why this context is being collected.
 */

import { useState } from 'react'
import businessScopingData from '../../../shared/data/businessScoping.json'

// Get the engagement context section from business scoping data
const engagementSection = businessScopingData.sections.find(s => s.id === 'engagement-context')

function EngagementDriverSection({ responses, onResponseChange }) {
  const [hoveredOption, setHoveredOption] = useState(null)

  const getResponse = (questionId) => {
    const response = responses?.[questionId]
    return typeof response === 'object' ? response.value : response
  }

  const getMultiResponse = (questionId) => {
    const response = responses?.[questionId]
    if (!response) return []
    const val = typeof response === 'object' ? response.value : response
    return Array.isArray(val) ? val : (val ? [val] : [])
  }

  const handleSelect = (questionId, option) => {
    onResponseChange(questionId, option.label, option.weight)
  }

  const handleMultiSelect = (questionId, option, maxSelections) => {
    const current = getMultiResponse(questionId)
    const isSelected = current.includes(option.label)
    let updated
    if (isSelected) {
      updated = current.filter(v => v !== option.label)
    } else {
      if (current.length >= maxSelections) return // at limit
      updated = [...current, option.label]
    }
    onResponseChange(questionId, updated, option.weight)
  }

  const getQuestionProgress = () => {
    if (!engagementSection) return { answered: 0, total: 0 }
    const answered = engagementSection.questions.filter(q => {
      if (q.responseType === 'multi-select') return getMultiResponse(q.id).length > 0
      return getResponse(q.id)
    }).length
    return { answered, total: engagementSection.questions.length }
  }

  const progress = getQuestionProgress()

  if (!engagementSection) {
    return null
  }

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Let's start with why</h2>
            <p className="text-blue-100 max-w-2xl">
              Understanding what brings you here helps us tailor recommendations to your specific situation.
              This context shapes how we prioritize findings and frame our guidance.
            </p>
          </div>
          <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
            <span className="text-2xl font-bold">{progress.answered}/{progress.total}</span>
            <p className="text-xs text-blue-100">answered</p>
          </div>
        </div>
      </div>

      {/* Note about scoring */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-gray-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-700 dark:text-gray-300">Note:</strong> These questions
              help shape your roadmap and report framing but do not affect your maturity scores.
              Your security assessment results are based solely on your control implementation.
            </p>
          </div>
        </div>
      </div>

      {/* Questions */}
      {engagementSection.questions.map((question, idx) => (
        <div key={question.id} className="card p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm mr-3">
              {idx + 1}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {question.question}
              </h3>
              {question.id === 'ed-1' && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Select up to {question.maxSelections || 3}. These affect how we prioritize recommendations and frame the roadmap.
                </p>
              )}
              {question.id === 'ed-2' && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  This helps us phase initiatives appropriately and set realistic expectations.
                </p>
              )}
              {question.id === 'ed-3' && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Select up to {question.maxSelections || 3}. This ensures our deliverables align with your expectations.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {question.options.map((option, optIdx) => {
              const isMulti = question.responseType === 'multi-select'
              const isSelected = isMulti
                ? getMultiResponse(question.id).includes(option.label)
                : getResponse(question.id) === option.label
              const isHovered = hoveredOption === `${question.id}-${optIdx}`
              const atLimit = isMulti && !isSelected && getMultiResponse(question.id).length >= (question.maxSelections || 3)

              return (
                <button
                  key={optIdx}
                  onClick={() => isMulti
                    ? handleMultiSelect(question.id, option, question.maxSelections || 3)
                    : handleSelect(question.id, option)
                  }
                  onMouseEnter={() => setHoveredOption(`${question.id}-${optIdx}`)}
                  onMouseLeave={() => setHoveredOption(null)}
                  disabled={atLimit}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : atLimit
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50 opacity-50 cursor-not-allowed'
                      : isHovered
                      ? 'border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-700/50'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 w-5 h-5 ${isMulti ? 'rounded' : 'rounded-full'} border-2 mr-3 mt-0.5 flex items-center justify-center ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300 dark:border-gray-500'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <span className="block font-medium text-gray-900 dark:text-white">
                        {option.label}
                      </span>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
          {question.responseType === 'multi-select' && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-right">
              {getMultiResponse(question.id).length} of {question.maxSelections || 3} selected
            </p>
          )}

          {/* Display "Other" responses from client intake */}
          {question.responseType === 'multi-select' && (() => {
            const otherEntry = getMultiResponse(question.id).find(v => typeof v === 'string' && v.startsWith('Other: '))
            if (!otherEntry) return null
            return (
              <div className="mt-3 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-teal-700 dark:text-teal-300">Client-provided response</p>
                    <p className="text-sm text-teal-800 dark:text-teal-200 mt-0.5">{otherEntry.replace('Other: ', '')}</p>
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      ))}

    </div>
  )
}

export default EngagementDriverSection
